/*
  Noctura v5 · <nc-explorer-component>: la Almohada Aster™ en 3D para girarla y ver cómo funciona.
  - El modelo se construye aquí (no hay archivo 3D). Forma tomada de las fotos de la almohada real que mandó Diego y
    de los renders del producto (2026-10-12):
    · base totalmente plana: rectángulo con esquinas redondeadas y, en el borde delantero, una lengüeta central
      angosta (apoyo del cuello, ~1/5 del largo) unida al cuerpo por dos curvas cóncavas largas y suaves (espacio
      para los hombros), que ocupan casi la mitad del borde delantero;
    · arriba, una meseta con una cuna central alrededor de un botón y pliegues que salen del botón hacia las esquinas
      y la lengüeta; la lengüeta baja redondeada hacia su punta;
    · en cada extremo, al medio, una rampa que baja hasta el borde (cubierta de malla negra) y, a sus lados,
      las esquinas que quedan altas: las "astas";
    · funda blanca acolchada en forma de estrella (centro, brazos a las cuatro esquinas y lengüeta, cuya cara
      delantera es blanca hasta abajo) y malla negra en los costados, las curvas de los hombros y las rampas.
    Es una representación con proporciones de las fotos, no una medición.
  - three.js (MIT) va como asset del tema (three.module.min.js) y se carga solo cuando la sección se acerca a la
    pantalla. Sin WebGL o sin JavaScript queda la foto y la lista de zonas (que también es la versión accesible).
  - Se gira arrastrando en horizontal (en móvil, el scroll vertical sigue funcionando) o con los botones.
  - Respeta prefers-reduced-motion (sin giro automático ni animaciones de cámara) y se pausa fuera de pantalla.
*/

const TAU = Math.PI * 2;
const RHO = 2.4; // radio del borde redondeado (cm aprox.)
const CENTER = [0, 0]; // centro para armar los anillos de la cara superior (el contorno es visible desde aquí)
const HALF_L = 30; // medio largo
const BACK_Z = 16; // borde trasero
const FRONT_Z = -15; // borde delantero del cuerpo
const TIP_Z = -23; // punta de la lengüeta
const BUTTON = [0, 0.5]; // botón del centro
const TAB_X = 6.5; // media anchura de la lengüeta
const CUT_X = 19; // donde la curva del hombro se une al borde delantero

/** Contorno de la base visto desde arriba (x = largo, z = profundidad; z negativo = borde delantero). */
function outlinePath() {
  const segs = [];
  const line = (x1, z1, x2, z2) => segs.push({ t: 'l', x1, z1, x2, z2 });
  const arc = (cx, cz, r, a1, a2) => segs.push({ t: 'a', cx, cz, r, a1: (a1 * Math.PI) / 180, a2: (a2 * Math.PI) / 180 });
  // cuarto de elipse en tramos rectos (así el muestreo sigue siendo parejo)
  const ell = (cx, cz, rx, rz, a1, a2) => {
    const pt = (a) => [cx + rx * Math.cos((a * Math.PI) / 180), cz + rz * Math.sin((a * Math.PI) / 180)];
    for (let k = 0; k < 40; k++) {
      const [x1, z1] = pt(a1 + ((a2 - a1) * k) / 40);
      const [x2, z2] = pt(a1 + ((a2 - a1) * (k + 1)) / 40);
      line(x1, z1, x2, z2);
    }
  };
  line(-26, BACK_Z, 26, BACK_Z); // borde trasero
  arc(26, BACK_Z - 4, 4, 90, 0); // esquina trasera derecha
  line(HALF_L, BACK_Z - 4, HALF_L, FRONT_Z + 4); // extremo derecho
  arc(26, FRONT_Z + 4, 4, 0, -90); // esquina delantera derecha
  line(26, FRONT_Z, CUT_X, FRONT_Z); // borde delantero derecho
  ell(CUT_X, -20.5, CUT_X - TAB_X, 5.5, 90, 180); // curva cóncava derecha (hombro): larga y suave
  arc(TAB_X - 2.5, -20.5, 2.5, 0, -90); // punta de la lengüeta
  line(TAB_X - 2.5, TIP_Z, 2.5 - TAB_X, TIP_Z);
  arc(2.5 - TAB_X, -20.5, 2.5, -90, -180);
  ell(-CUT_X, -20.5, CUT_X - TAB_X, 5.5, 0, 90); // curva cóncava izquierda (hombro)
  line(-CUT_X, FRONT_Z, -26, FRONT_Z); // borde delantero izquierdo
  arc(-26, FRONT_Z + 4, 4, -90, -180); // esquina delantera izquierda
  line(-HALF_L, FRONT_Z + 4, -HALF_L, BACK_Z - 4); // extremo izquierdo
  arc(-26, BACK_Z - 4, 4, 180, 90); // esquina trasera izquierda
  return segs;
}

function sampleOutline(n) {
  const segs = outlinePath().map((s) => ({ ...s, len: s.t === 'l' ? Math.hypot(s.x2 - s.x1, s.z2 - s.z1) : Math.abs(s.a2 - s.a1) * s.r }));
  const total = segs.reduce((a, s) => a + s.len, 0);
  const pts = [];
  for (let i = 0; i < n; i++) {
    let d = (i / n) * total;
    let k = 0;
    while (d > segs[k].len && k < segs.length - 1) { d -= segs[k].len; k++; }
    const s = segs[k];
    const t = s.len ? d / s.len : 0;
    if (s.t === 'l') pts.push([s.x1 + (s.x2 - s.x1) * t, s.z1 + (s.z2 - s.z1) * t]);
    else {
      const a = s.a1 + (s.a2 - s.a1) * t;
      pts.push([s.cx + s.r * Math.cos(a), s.cz + s.r * Math.sin(a)]);
    }
  }
  return pts.map((p, i) => {
    const a = pts[(i - 1 + n) % n];
    const b = pts[(i + 1) % n];
    let nx = b[1] - a[1];
    let nz = -(b[0] - a[0]);
    const l = Math.hypot(nx, nz) || 1;
    nx /= l; nz /= l;
    if (nx * (p[0] - CENTER[0]) + nz * (p[1] - CENTER[1]) < 0) { nx = -nx; nz = -nz; } // hacia afuera
    return { x: p[0], z: p[1], nx, nz };
  });
}

const smooth = (a, b, x) => {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
};

/**
 * Rampa de un extremo (0 fuera, 1 en el borde del extremo). Entra hacia el centro en forma de cuña con lados
 * cóncavos (como las cuñas negras de las fotos), centrada en la profundidad del botón.
 */
const RAMP_HALF = 12; // media anchura de la rampa en el borde del extremo
const RAMP_DEPTH = 10; // cuánto entra hacia el centro
/** Desde qué |x| empieza la malla de la rampa a la profundidad z (más allá de RAMP_HALF, nunca). */
function rampEdge(z) {
  const t = Math.min(1, Math.abs(z - BUTTON[1]) / RAMP_HALF);
  return HALF_L - RAMP_DEPTH * Math.pow(1 - t, 1.5);
}
function ramp(x, z) {
  const xw = rampEdge(z);
  const ax = Math.abs(x);
  if (ax <= xw) return 0;
  return Math.min(1, (ax - xw) / Math.max(0.001, HALF_L - xw));
}

/** Distancia de (x, z) al segmento a-b y posición t a lo largo del segmento (para los pliegues). */
function segDist(x, z, ax, az, bx, bz) {
  const vx = bx - ax;
  const vz = bz - az;
  const t = Math.max(0, Math.min(1, ((x - ax) * vx + (z - az) * vz) / (vx * vx + vz * vz)));
  return [Math.hypot(x - (ax + vx * t), z - (az + vz * t)), t];
}
const CREASES = [[27, -12], [-27, -12], [27, 13], [-27, 13], [0, TIP_Z + 2]];

/** Altura de la cara superior en (x, z). */
function height(x, z) {
  const ax = Math.abs(x);
  const bz = z - BUTTON[1];
  let h = 11.2; // meseta
  h += 0.9 * Math.exp(-(((ax - 26.5) ** 2) / (2 * 3.4 * 3.4))) * smooth(7, 12.5, Math.abs(bz)); // astas: esquinas algo más altas
  h -= 2.7 * Math.exp(-((x * x) / (2 * 8.5 * 8.5) + (bz * bz) / (2 * 7.5 * 7.5))); // cuna central
  h -= 1.4 * Math.exp(-(x * x + bz * bz) / (2 * 1.6 * 1.6)); // hundido del botón
  for (const [cx, cz] of CREASES) { // pliegues de la funda que salen del botón
    const [d, t] = segDist(x, z, BUTTON[0], BUTTON[1], cx, cz);
    h -= 0.7 * Math.exp(-(d * d) / (2 * 0.9 * 0.9)) * smooth(0.04, 0.16, t) * (1 - 0.7 * t);
  }
  const r = ramp(x, z);
  h -= 4.6 * r * r * (3 - 2 * r); // rampa de malla en cada extremo
  h -= 5.6 * smooth(-13, -22.5, z) * Math.exp(-(x * x) / (2 * 10 * 10)); // la lengüeta baja en rampa suave hacia la punta
  h -= 0.8 * smooth(-12, FRONT_Z, z) * smooth(TAB_X, TAB_X + 5, ax) * (1 - smooth(CUT_X, CUT_X + 5, ax)); // borde sobre los hombros, más blando
  return h;
}

function quiltTexture(THREE) {
  const c = document.createElement('canvas');
  c.width = c.height = 256;
  const g = c.getContext('2d');
  g.fillStyle = '#f6f3ee';
  g.fillRect(0, 0, 256, 256);
  g.strokeStyle = '#d8d1c4';
  g.lineWidth = 3;
  for (let i = -256; i <= 512; i += 32) {
    g.beginPath(); g.moveTo(i, 0); g.lineTo(i + 256, 256); g.stroke();
    g.beginPath(); g.moveTo(i, 256); g.lineTo(i + 256, 0); g.stroke();
  }
  const t = new THREE.CanvasTexture(c);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 4;
  return t;
}

function meshTexture(THREE) {
  const c = document.createElement('canvas');
  c.width = c.height = 128;
  const g = c.getContext('2d');
  g.fillStyle = '#121418';
  g.fillRect(0, 0, 128, 128);
  g.fillStyle = '#262a32';
  for (let y = 0; y < 128; y += 8) {
    for (let x = (y / 8) % 2 ? 4 : 0; x < 128; x += 8) {
      g.beginPath(); g.ellipse(x, y, 2.4, 1.8, 0, 0, TAU); g.fill();
    }
  }
  const t = new THREE.CanvasTexture(c);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

function shadowTexture(THREE) {
  const c = document.createElement('canvas');
  c.width = c.height = 256;
  const g = c.getContext('2d');
  const grd = g.createRadialGradient(128, 128, 10, 128, 128, 128);
  grd.addColorStop(0, 'rgba(11,22,40,0.42)');
  grd.addColorStop(0.55, 'rgba(11,22,40,0.16)');
  grd.addColorStop(1, 'rgba(11,22,40,0)');
  g.fillStyle = grd;
  g.fillRect(0, 0, 256, 256);
  return new THREE.CanvasTexture(c);
}

/** Cara delantera de la lengüeta (blanca hasta abajo, como en las fotos). */
const isTabFront = (p) => p.z < -20.4 && Math.abs(p.x) < TAB_X + 0.1;
/** Pared trasera: blanca, con el cierre de la funda (foto de la funda real). */
const isBackWall = (p) => p.z > BACK_Z - 2.2;

const UV = 12; // cm por baldosa de textura: rombos del acolchado de ~1,5 cm, como en la funda real
const RIM_H = 3.6; // cuánto baja la funda blanca por el borde antes de la pared (en lo plano)
/**
 * Perfil del borde en el punto p del contorno: curva de Bézier que sale de la cara superior con su misma pendiente
 * (en la lengüeta, que baja en rampa, sigue bajando) y llega vertical a la pared. Devuelve el punto interior (qx, qz),
 * la altura donde empieza la pared (yw) y las muestras [distancia hacia afuera, altura, largo recorrido].
 */
function rimProfile(p, steps) {
  const qx = p.x - p.nx * RHO;
  const qz = p.z - p.nz * RHO;
  const hq = height(qx, qz);
  const e = 0.4;
  const slope = (height(qx + p.nx * e, qz + p.nz * e) - height(qx - p.nx * e, qz - p.nz * e)) / (2 * e);
  const s = Math.max(-1.6, Math.min(0.3, slope));
  const yw = Math.max(RHO + 0.4, hq - RIM_H);
  const k = 0.552;
  const p1y = Math.max(yw + 0.2, hq + k * RHO * s);
  const c = [[0, hq], [k * RHO, p1y], [RHO, yw + k * (p1y - yw)], [RHO, yw]];
  const pts = [];
  let len = 0;
  let prev = c[0];
  for (let j = 0; j <= steps; j++) {
    const t = j / steps;
    const w = [(1 - t) ** 3, 3 * (1 - t) ** 2 * t, 3 * (1 - t) * t * t, t ** 3];
    const pt = [0, 1].map((a) => w[0] * c[0][a] + w[1] * c[1][a] + w[2] * c[2][a] + w[3] * c[3][a]);
    len += Math.hypot(pt[0] - prev[0], pt[1] - prev[1]);
    prev = pt;
    pts.push([pt[0], pt[1], len]);
  }
  return { qx, qz, yw, pts };
}
/** > 0: malla negra de la rampa; <= 0: funda blanca. */
const meshSide = (x, z) => Math.abs(x) - rampEdge(z);

/**
 * Geometría cerrada de la almohada. Grupo 0: funda blanca (estrella de arriba, bordes redondeados fuera de las rampas
 * y cara delantera de la lengüeta). Grupo 1: malla negra (rampas de los extremos, costados, curvas de los hombros y base).
 * Anillos de la cara superior: del centro a la curva desplazada RHO hacia adentro; el borde y los costados siguen la
 * normal del contorno, así el redondeo es parejo también en las curvas cóncavas.
 */
function buildPillow(THREE) {
  const N = 360; // puntos del contorno
  const TOP = 34;
  const ARC = 9;
  const WALL = 4;
  const outline = sampleOutline(N);
  const positions = [];
  const uvs = [];
  const heights = [];
  const rows = [];
  let perim = 0;
  const along = outline.map((p, i) => {
    if (i) perim += Math.hypot(p.x - outline[i - 1].x, p.z - outline[i - 1].z);
    return perim;
  });
  const ring = (kind, fn) => {
    const start = positions.length / 3;
    for (let i = 0; i <= N; i++) {
      const p = outline[i % N];
      const [x, y, z, u, v] = fn(p, i % N);
      positions.push(x, y, z);
      if (u !== undefined) uvs.push(u, v);
      else if (kind === 'top') uvs.push(x / UV, z / UV);
      else uvs.push((i < N ? along[i] : perim) / UV, y / UV);
      heights.push(y);
    }
    rows.push([kind, start]);
  };
  const inner = (p) => [p.x - p.nx * RHO, p.z - p.nz * RHO];
  for (let j = 0; j <= TOP; j++) {
    const u = 1 - Math.pow(1 - j / TOP, 1.6);
    ring('top', (p) => {
      const [qx, qz] = inner(p);
      const x = CENTER[0] + (qx - CENTER[0]) * u;
      const z = CENTER[1] + (qz - CENTER[1]) * u;
      return [x, height(x, z), z];
    });
  }
  // Borde: sale de la cara superior siguiendo su pendiente y baja suave hasta la pared. La textura se "desdobla" por
  // el borde (sigue la distancia recorrida), así el acolchado no se estira.
  const rims = outline.map((p) => rimProfile(p, ARC));
  for (let k = 1; k <= ARC; k++) {
    ring('edge', (p, i) => {
      const r = rims[i];
      const [d, y, len] = r.pts[k];
      return [r.qx + p.nx * d, y, r.qz + p.nz * d, (r.qx + p.nx * len) / UV, (r.qz + p.nz * len) / UV];
    });
  }
  for (let k = 0; k <= WALL; k++) {
    ring('wall', (p, i) => {
      const top = rims[i].yw;
      return [p.x, top - (k / WALL) * (top - RHO), p.z];
    });
  }
  for (let k = 1; k <= 3; k++) {
    const phi = (k / 3) * (Math.PI / 2);
    ring('base', (p) => {
      const [qx, qz] = inner(p);
      return [qx + p.nx * RHO * Math.cos(phi), RHO - RHO * Math.sin(phi), qz + p.nz * RHO * Math.cos(phi)];
    });
  }
  ring('base', () => [CENTER[0], 0, CENTER[1]]); // base plana

  const P = (k) => [positions[k * 3], positions[k * 3 + 1], positions[k * 3 + 2]];
  const white = [];
  const black = [];
  const topEnd = (TOP + 1) * (N + 1); // los primeros vértices son los de la cara superior
  const topCuts = new Set();
  // Los triángulos de arriba y del borde que cruzan el límite de la rampa se cortan justo en el límite: la línea entre
  // la funda blanca y la malla queda curva y limpia, sin escalones, y en el borde de los extremos baja en diagonal
  // (no en vertical). Cada corte se comparte con el triángulo vecino.
  const cuts = new Map();
  const cutAt = (i, j, fi, fj, onTop) => {
    const key = i < j ? `${i}_${j}` : `${j}_${i}`;
    if (cuts.has(key)) return cuts.get(key);
    const s = fi / (fi - fj);
    const [x, yl, z] = [0, 1, 2].map((a) => positions[i * 3 + a] + (positions[j * 3 + a] - positions[i * 3 + a]) * s);
    const y = onTop ? height(x, z) : yl;
    const k = positions.length / 3;
    positions.push(x, y, z);
    if (onTop) uvs.push(x / UV, z / UV);
    else uvs.push(...[0, 1].map((a) => uvs[i * 2 + a] + (uvs[j * 2 + a] - uvs[i * 2 + a]) * s));
    heights.push(y);
    if (onTop) topCuts.add(k);
    cuts.set(key, k);
    return k;
  };
  const meshTri = (v, onTop) => {
    const f = v.map((k) => meshSide(positions[k * 3], positions[k * 3 + 2]));
    const side = f.map((value) => value > 0);
    if (side[0] === side[1] && side[1] === side[2]) {
      (side[0] ? black : white).push(...v);
      return;
    }
    const lone = side[1] === side[2] ? 0 : side[0] === side[2] ? 1 : 2; // el vértice que queda solo va primero
    const a = v[lone];
    const b = v[(lone + 1) % 3];
    const c = v[(lone + 2) % 3];
    const ab = cutAt(a, b, f[lone], f[(lone + 1) % 3], onTop);
    const ac = cutAt(a, c, f[lone], f[(lone + 2) % 3], onTop);
    (side[lone] ? black : white).push(a, ab, ac);
    (side[lone] ? white : black).push(ab, b, c, ab, c, ac);
  };
  for (let r = 0; r < rows.length - 1; r++) {
    const [kindA, a0] = rows[r];
    const [kindB, b0] = rows[r + 1];
    for (let i = 0; i < N; i++) {
      const a = a0 + i;
      const b = b0 + i;
      const p = outline[i];
      let isWhite;
      if ((kindA === 'top' && kindB === 'top') || kindB === 'edge') {
        const onTop = kindB === 'top';
        meshTri([a, a + 1, b], onTop);
        meshTri([a + 1, b + 1, b], onTop);
        continue;
      } else if (kindB === 'wall' || (kindA === 'wall' && kindB === 'base')) {
        isWhite = isTabFront(p) || isBackWall(p);
      } else {
        isWhite = false;
      }
      (isWhite ? white : black).push(a, a + 1, b, a + 1, b + 1, b);
    }
  }
  // orientación: un triángulo de la cara superior debe mirar hacia arriba; si no, se invierten todos
  const r0 = rows[12][1];
  const r1 = rows[13][1];
  const A = P(r0 + 40);
  const B = P(r0 + 41);
  const C = P(r1 + 40);
  const ny = (B[2] - A[2]) * (C[0] - A[0]) - (B[0] - A[0]) * (C[2] - A[2]);
  if (ny < 0) {
    for (const list of [white, black]) {
      for (let i = 0; i < list.length; i += 3) { const t = list[i + 1]; list[i + 1] = list[i + 2]; list[i + 2] = t; }
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geo.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  // sombreado suave según la altura (lo bajo, un poco más oscuro) y en los pliegues y el botón (donde la superficie
  // se hunde): ayuda a leer el relieve
  const colors = new Float32Array(positions.length);
  const d = 1.1;
  for (let i = 0; i < heights.length; i++) {
    let k = 0.74 + 0.26 * smooth(6.5, 12, heights[i]);
    if (i < topEnd || topCuts.has(i)) {
      const x = positions[i * 3];
      const z = positions[i * 3 + 2];
      const lap = (height(x + d, z) + height(x - d, z) + height(x, z + d) + height(x, z - d) - 4 * heights[i]) / (d * d);
      k *= 1 - Math.min(0.2, Math.max(0, lap) * 0.3);
    }
    colors[i * 3] = k;
    colors[i * 3 + 1] = k;
    colors[i * 3 + 2] = k * 0.985;
  }
  geo.userData.baseColors = colors.slice();
  geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  geo.setIndex([...white, ...black]);
  geo.addGroup(0, white.length, 0);
  geo.addGroup(white.length, black.length, 1);
  geo.computeVertexNormals();
  // vértices repetidos (fin del borde = inicio de la pared, y la costura del contorno): misma normal en ambos,
  // para que no se vea una arista dura
  const nrm = geo.getAttribute('normal');
  const merge = (a, b) => {
    const x = nrm.getX(a) + nrm.getX(b);
    const y = nrm.getY(a) + nrm.getY(b);
    const z = nrm.getZ(a) + nrm.getZ(b);
    const l = Math.hypot(x, y, z) || 1;
    nrm.setXYZ(a, x / l, y / l, z / l);
    nrm.setXYZ(b, x / l, y / l, z / l);
  };
  const rimLast = rows[TOP + ARC][1];
  const wallFirst = rows[TOP + ARC + 1][1];
  for (let i = 0; i <= N; i++) merge(rimLast + i, wallFirst + i);
  for (const [, start] of rows) merge(start, start + N);
  geo.userData.heights = heights;
  return geo;
}

/** Zonas: punto 3D, normal aproximada (para atenuar si queda detrás) y vista de cámara [azimut, elevación] al elegirla. */
const ZONES = {
  wings: { point: [-21, height(-21, -9.5) + 0.3, -9.5], normal: [-0.2, 1, -0.3], view: [-50, 28] },
  center: { point: [0, height(0, BUTTON[1]) + 0.3, BUTTON[1]], normal: [0, 1, 0], view: [0, 62] },
  neck: { point: [0, height(0, -17.5) + 0.3, -17.5], normal: [0, 1, -0.6], view: [0, 24] },
  cuts: { point: [-9.4, 4.2, -17], normal: [-0.46, 0, -0.89], view: [-28, 12] },
  mesh: { point: [-27.5, height(-27.5, 3) + 0.3, 3], normal: [-0.6, 1, 0], view: [-84, 26] },
};

class NcExplorerComponent extends HTMLElement {
  connectedCallback() {
    this.stage = this.querySelector('[data-stage]');
    this.zoneButtons = [...this.querySelectorAll('[data-zone]')];
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.zoneButtons.forEach((btn) => btn.addEventListener('click', () => this.select(btn.dataset.zone)));
    const first = this.querySelector('.nc-explorer__zone')?.getAttribute('data-zone');
    if (first) this.setActive(first); // sin JavaScript se ven todos los textos
    this.querySelector('[data-rotate-left]')?.addEventListener('click', () => this.nudge(-45));
    this.querySelector('[data-rotate-right]')?.addEventListener('click', () => this.nudge(45));
    this.querySelector('[data-reset]')?.addEventListener('click', () => this.goTo(-32, 32));
    this.heightToggle = this.querySelector('[data-heights]');
    this.heightToggle?.addEventListener('click', () => this.toggleHeights());
    this.io = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        this.visible = entry.isIntersecting;
        if (entry.isIntersecting && !this.started) this.start();
        if (entry.isIntersecting) this.kick();
      }
    }, { rootMargin: '300px 0px' });
    this.io.observe(this);
    document.addEventListener('visibilitychange', () => this.kick());
  }

  disconnectedCallback() {
    this.io?.disconnect();
    this.resizeObserver?.disconnect();
    this.renderer?.dispose();
  }

  async start() {
    this.started = true;
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl2') || canvas.getContext('webgl');
    if (!gl) return;
    let THREE;
    try {
      THREE = await import(this.dataset.three);
    } catch (error) {
      return; // queda la foto
    }
    this.THREE = THREE;
    const renderer = new THREE.WebGLRenderer({ canvas, context: gl, antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 0.95;
    this.renderer = renderer;

    const scene = new THREE.Scene();
    this.scene = scene;
    const camera = new THREE.PerspectiveCamera(28, 1, 1, 1000);
    this.camera = camera;
    this.target = new THREE.Vector3(0, 6, -3.5);

    scene.add(new THREE.HemisphereLight(0xffffff, 0xbdb3a3, 0.85));
    const key = new THREE.DirectionalLight(0xfff4e2, 2.7);
    key.position.set(-45, 55, -40);
    scene.add(key);
    const rim = new THREE.DirectionalLight(0xd9e4ff, 0.9);
    rim.position.set(50, 30, 60);
    scene.add(rim);

    const quilt = quiltTexture(THREE);
    const mesh = meshTexture(THREE);
    mesh.repeat.set(UV / 3.2, UV / 3.2); // agujeros de la malla de ~2 mm, iguales en costados y rampas
    this.matTop = new THREE.MeshStandardMaterial({ map: quilt, bumpMap: quilt, bumpScale: 0.6, roughness: 0.92, vertexColors: true });
    const matSide = new THREE.MeshStandardMaterial({ map: mesh, bumpMap: mesh, bumpScale: 0.8, roughness: 0.95, color: 0xffffff });
    this.matSide = matSide;
    const geo = buildPillow(THREE);
    this.geo = geo;
    const pillow = new THREE.Mesh(geo, [this.matTop, matSide]);
    scene.add(pillow);
    // botón gris del centro (como en la almohada real)
    const button = new THREE.Mesh(
      new THREE.CylinderGeometry(0.85, 0.85, 0.35, 28),
      new THREE.MeshStandardMaterial({ color: 0x8d939b, roughness: 0.6 })
    );
    button.position.set(BUTTON[0], height(BUTTON[0], BUTTON[1]) + 0.05, BUTTON[1]);
    scene.add(button);

    const shadow = new THREE.Mesh(
      new THREE.PlaneGeometry(92, 64),
      new THREE.MeshBasicMaterial({ map: shadowTexture(THREE), transparent: true, depthWrite: false })
    );
    shadow.rotation.x = -Math.PI / 2;
    shadow.position.y = 0.02;
    scene.add(shadow);

    this.stage.prepend(canvas);
    canvas.setAttribute('aria-hidden', 'true');
    canvas.className = 'nc-explorer__canvas';
    this.canvas = canvas;
    this.classList.add('is-ready');

    this.az = -32;
    this.el = 32;
    this.dist = 112;
    this.auto = !this.reduced;
    this.bindDrag(canvas);
    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(this.stage);
    this.resize();
    this.kick();
  }

  resize() {
    if (!this.renderer) return;
    const w = this.stage.clientWidth;
    const h = this.stage.clientHeight;
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.dist = w < 520 ? 132 : 112;
    this.camera.updateProjectionMatrix();
    this.kick();
  }

  bindDrag(canvas) {
    let startX = 0;
    let startY = 0;
    let startAz = 0;
    let startEl = 0;
    let dragging = false;
    let decided = false;
    canvas.addEventListener('pointerdown', (event) => {
      dragging = true;
      decided = event.pointerType === 'mouse';
      startX = event.clientX;
      startY = event.clientY;
      startAz = this.az;
      startEl = this.el;
      this.auto = false;
      this.anim = null;
    });
    canvas.addEventListener('pointermove', (event) => {
      if (!dragging) return;
      const dx = event.clientX - startX;
      const dy = event.clientY - startY;
      if (!decided) {
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
        decided = true;
        if (Math.abs(dy) > Math.abs(dx)) { dragging = false; return; } // gesto vertical: es scroll
        canvas.setPointerCapture(event.pointerId);
      }
      this.az = startAz + dx * 0.4; // la almohada sigue al dedo
      if (event.pointerType === 'mouse') this.el = Math.min(70, Math.max(6, startEl + dy * 0.25));
      this.kick();
    });
    const end = () => { dragging = false; };
    canvas.addEventListener('pointerup', end);
    canvas.addEventListener('pointercancel', end);
  }

  nudge(deg) {
    this.auto = false;
    this.goTo(this.az + deg, this.el);
  }

  goTo(az, el) {
    if (this.reduced || !this.renderer) {
      this.az = az;
      this.el = el;
      this.kick();
      return;
    }
    const delta = ((((az - this.az) % 360) + 540) % 360) - 180;
    this.anim = { fromAz: this.az, toAz: this.az + delta, fromEl: this.el, toEl: el, t0: performance.now(), dur: 700 };
    this.kick();
  }

  setActive(id) {
    this.active = id;
    this.zoneButtons.forEach((btn) => btn.setAttribute('aria-pressed', btn.dataset.zone === id ? 'true' : 'false'));
    this.querySelectorAll('[data-zone-text]').forEach((el) => { el.hidden = el.dataset.zoneText !== id; });
  }

  select(id) {
    this.auto = false;
    this.setActive(id);
    const zone = ZONES[id];
    if (zone) this.goTo(zone.view[0], zone.view[1]);
  }

  toggleHeights() {
    if (!this.geo) return;
    this.heights = !this.heights;
    this.heightToggle.setAttribute('aria-pressed', this.heights ? 'true' : 'false');
    this.classList.toggle('show-heights', this.heights);
    const THREE = this.THREE;
    const color = this.geo.getAttribute('color');
    const hs = this.geo.userData.heights;
    const low = new THREE.Color('#2f4a7a');
    const high = new THREE.Color('#e3b453');
    const tmp = new THREE.Color();
    for (let i = 0; i < hs.length; i++) {
      if (this.heights) {
        const t = Math.min(1, Math.max(0, (hs[i] - 6) / 6.2));
        tmp.copy(low).lerp(high, t);
        color.setXYZ(i, tmp.r * 1.6, tmp.g * 1.6, tmp.b * 1.6);
      } else {
        const base = this.geo.userData.baseColors;
        color.setXYZ(i, base[i * 3], base[i * 3 + 1], base[i * 3 + 2]);
      }
    }
    color.needsUpdate = true;
    this.matTop.map = this.heights ? null : this.matTop.bumpMap;
    this.matTop.needsUpdate = true;
    this.matSide.vertexColors = this.heights; // las rampas de malla también muestran su altura
    this.matSide.map = this.heights ? null : this.matSide.bumpMap;
    this.matSide.needsUpdate = true;
    this.kick();
  }

  kick() {
    if (!this.renderer || this.raf) return;
    this.raf = requestAnimationFrame((t) => this.frame(t));
  }

  frame(time) {
    this.raf = null;
    if (!this.visible || document.hidden) return;
    let keep = false;
    if (this.anim) {
      const p = Math.min(1, (time - this.anim.t0) / this.anim.dur);
      const e = 1 - Math.pow(1 - p, 3);
      this.az = this.anim.fromAz + (this.anim.toAz - this.anim.fromAz) * e;
      this.el = this.anim.fromEl + (this.anim.toEl - this.anim.fromEl) * e;
      if (p >= 1) this.anim = null;
      keep = true;
    } else if (this.auto) {
      this.az += 0.12;
      keep = true;
    }
    const THREE = this.THREE;
    const az = THREE.MathUtils.degToRad(this.az);
    const el = THREE.MathUtils.degToRad(this.el);
    this.camera.position.set(
      this.target.x + this.dist * Math.cos(el) * Math.sin(az),
      this.target.y + this.dist * Math.sin(el),
      this.target.z - this.dist * Math.cos(el) * Math.cos(az)
    );
    this.camera.lookAt(this.target);
    this.renderer.render(this.scene, this.camera);
    this.placeHotspots();
    if (keep) this.kick();
  }

  placeHotspots() {
    const THREE = this.THREE;
    const w = this.stage.clientWidth;
    const h = this.stage.clientHeight;
    const v = new THREE.Vector3();
    const n = new THREE.Vector3();
    const toCam = new THREE.Vector3();
    const spots = [];
    for (const btn of this.querySelectorAll('[data-hotspot]')) {
      const zone = ZONES[btn.dataset.hotspot];
      if (!zone) continue;
      v.set(...zone.point);
      n.set(...zone.normal).normalize();
      toCam.copy(this.camera.position).sub(v).normalize();
      const facing = n.dot(toCam);
      v.project(this.camera);
      spots.push({ btn, x: ((v.x + 1) / 2) * w, y: ((1 - v.y) / 2) * h, facing });
    }
    // en algunos ángulos dos números quedan encimados (p. ej. 1 y 5): se separan lo justo para que se lean y se toquen
    const min = 38;
    for (let pass = 0; pass < 3; pass++) {
      for (let i = 0; i < spots.length; i++) {
        for (let j = i + 1; j < spots.length; j++) {
          const a = spots[i];
          const b = spots[j];
          const dx = b.x - a.x;
          const dy = b.y - a.y;
          const d = Math.hypot(dx, dy);
          if (d >= min) continue;
          const ux = d ? dx / d : 1;
          const uy = d ? dy / d : 0;
          const push = (min - d) / 2;
          a.x -= ux * push;
          a.y -= uy * push;
          b.x += ux * push;
          b.y += uy * push;
        }
      }
    }
    for (const { btn, x, y, facing } of spots) {
      btn.style.transform = `translate(${x}px, ${y}px) translate(-50%, -50%)`;
      btn.classList.toggle('is-behind', facing < -0.05);
    }
  }
}

if (!customElements.get('nc-explorer-component')) {
  customElements.define('nc-explorer-component', NcExplorerComponent);
}
